import { useEffect, useRef, useState } from 'react';
import {
  X,
  Calendar,
  User,
  Tag,
  AlertCircle,
  CheckCircle2,
  Clock,
  Hash,
  Sparkles,
  MessageSquare,
  Link2,
  ExternalLink,
  Pencil,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Task } from '../../../types/task';
import {
  taskStatusMeta,
  taskPriorityMeta,
  taskTypeMeta,
} from '@/shared/lib/classificationMeta';
import { Badge } from '@/shared/components/Badge';
import { cn } from '@/shared/utils/cn';
import { formatDateTime } from '@/shared/utils/date';
import { todayIso } from '@/shared/utils/today';
import { taskApi } from '@/shared/services/taskApi';
import { getErrorMessage } from '@/shared/services/api';
import { Spinner } from '@/shared/components/Spinner';
import { TaskPriority, TaskStatus, TaskType } from '../../../types/task';
import toast from 'react-hot-toast';

interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  /** Called after a successful save so the board can pick up the change (SC-262). */
  onTaskUpdated?: () => void;
}

/** The subset of a task SC-262 makes editable. */
interface TaskEditDraft {
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  type: TaskType;
  dueDate: string;
  storyPoints: string;
  acceptanceCriteria: string;
}

/** An ISO timestamp reduced to the `yyyy-MM-dd` a date input expects. */
const toDateInput = (value?: string): string => (value ? value.slice(0, 10) : '');

const draftFrom = (task: Task): TaskEditDraft => ({
  title: task.title,
  description: task.description,
  status: task.status,
  priority: task.priority,
  type: task.type,
  dueDate: toDateInput(task.dueDate),
  storyPoints: task.storyPoints != null ? String(task.storyPoints) : '',
  acceptanceCriteria: task.acceptanceCriteria ?? '',
});

const PRIORITY_SEGMENTS = 5;

/** Monochrome 1–5 level glyph — non-color cue for priority. */
function PriorityGlyph({ level }: { level: number }) {
  return (
    <span className="flex items-center gap-0.5 text-foreground" aria-hidden="true">
      {Array.from({ length: PRIORITY_SEGMENTS }).map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-3 w-1 rounded-[1px] bg-current',
            i < level ? 'opacity-100' : 'opacity-20'
          )}
        />
      ))}
    </span>
  );
}

/** A labelled property tile for the rail / metadata grid. */
function PropertyTile({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Calendar;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-4">
      <div className="mb-1.5 flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

export function TaskDetailsModal({ isOpen, onClose, task, onTaskUpdated }: TaskDetailsModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<TaskEditDraft | null>(null);
  const [saving, setSaving] = useState(false);

  // Leaving edit mode open across a change of task would show one task's values under
  // another's heading.
  useEffect(() => {
    setIsEditing(false);
    setDraft(null);
  }, [task?.id, isOpen]);

  // Close on Escape and move focus into the panel when opened.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !task) return null;

  const startEdit = () => {
    setDraft(draftFrom(task));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setDraft(null);
  };

  const handleSave = async () => {
    if (!draft) return;

    if (!draft.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!draft.description.trim()) {
      toast.error('Description is required');
      return;
    }
    // SC-264 applies to edits too, but only to a date being moved into the past - an
    // already-overdue task must stay editable. The server enforces the same rule.
    if (draft.dueDate && draft.dueDate < todayIso() && draft.dueDate !== toDateInput(task.dueDate)) {
      toast.error('Due date cannot be in the past.');
      return;
    }

    const parsedPoints = draft.storyPoints.trim() === '' ? undefined : Number(draft.storyPoints);
    if (parsedPoints !== undefined && (!Number.isFinite(parsedPoints) || parsedPoints < 0)) {
      toast.error('Story points must be a positive number');
      return;
    }

    setSaving(true);
    try {
      await taskApi.updateTask(task.id, {
        title: draft.title.trim(),
        description: draft.description.trim(),
        status: draft.status,
        priority: draft.priority,
        type: draft.type,
        dueDate: draft.dueDate || undefined,
        storyPoints: parsedPoints,
        acceptanceCriteria: draft.acceptanceCriteria.trim() || undefined,
      });
      toast.success('Task updated');
      cancelEdit();
      onTaskUpdated?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update task'));
    } finally {
      setSaving(false);
    }
  };

  const status = taskStatusMeta[task.status];
  const priority = taskPriorityMeta[task.priority];
  const typeMeta = taskTypeMeta[task.type];
  const TypeIcon = typeMeta.icon;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-secondary-950/50 backdrop-blur-xs"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={task.title}
        className="surface-overlay animate-slide-up relative flex h-full w-full max-w-lg flex-col border-l border-border shadow-modal outline-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
              <TypeIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold leading-snug text-foreground">{task.title}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={status.pill}>
                  <span className={cn('h-2 w-2 rounded-full', status.dot)} aria-hidden="true" />
                  {status.label}
                </span>
                <Badge variant="neutral">{typeMeta.label}</Badge>
                {task.isAiGenerated && (
                  <Badge variant="brand" className="gap-1">
                    <Sparkles className="h-3 w-3" aria-hidden="true" />
                    AI Generated
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {!isEditing && (
              <button
                onClick={startEdit}
                className="btn-icon"
                aria-label="Edit task"
                title="Edit task"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            <button onClick={onClose} className="btn-icon" aria-label="Close panel">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {isEditing && draft ? (
            /* Edit form (SC-262) */
            <div className="space-y-4">
              <div>
                <label className="label" htmlFor="edit-task-title">Title</label>
                <input
                  id="edit-task-title"
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="input mt-1.5"
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-task-description">Description</label>
                <textarea
                  id="edit-task-description"
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={5}
                  className="input mt-1.5"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="edit-task-status">Status</label>
                  <select
                    id="edit-task-status"
                    value={draft.status}
                    onChange={(e) => setDraft({ ...draft, status: e.target.value as TaskStatus })}
                    className="input mt-1.5"
                  >
                    {Object.values(TaskStatus).map((v) => (
                      <option key={v} value={v}>{taskStatusMeta[v].label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="edit-task-priority">Priority</label>
                  <select
                    id="edit-task-priority"
                    value={draft.priority}
                    onChange={(e) => setDraft({ ...draft, priority: e.target.value as TaskPriority })}
                    className="input mt-1.5"
                  >
                    {Object.values(TaskPriority).map((v) => (
                      <option key={v} value={v}>{taskPriorityMeta[v].label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="edit-task-type">Type</label>
                  <select
                    id="edit-task-type"
                    value={draft.type}
                    onChange={(e) => setDraft({ ...draft, type: e.target.value as TaskType })}
                    className="input mt-1.5"
                  >
                    {Object.values(TaskType).map((v) => (
                      <option key={v} value={v}>{taskTypeMeta[v].label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="edit-task-points">Story Points</label>
                  <input
                    id="edit-task-points"
                    type="number"
                    min={0}
                    value={draft.storyPoints}
                    onChange={(e) => setDraft({ ...draft, storyPoints: e.target.value })}
                    className="input mt-1.5"
                  />
                </div>
              </div>

              <div>
                <label className="label" htmlFor="edit-task-duedate">Due Date</label>
                <input
                  id="edit-task-duedate"
                  type="date"
                  value={draft.dueDate}
                  // An already-overdue task keeps its own date reachable so the field is not
                  // impossible to leave unchanged (SC-264).
                  min={toDateInput(task.dueDate) && toDateInput(task.dueDate) < todayIso()
                    ? toDateInput(task.dueDate)
                    : todayIso()}
                  onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                  className="input mt-1.5"
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-task-criteria">Acceptance Criteria</label>
                <textarea
                  id="edit-task-criteria"
                  value={draft.acceptanceCriteria}
                  onChange={(e) => setDraft({ ...draft, acceptanceCriteria: e.target.value })}
                  rows={4}
                  className="input mt-1.5"
                />
              </div>
            </div>
          ) : (
          <>
          {/* Property rail */}
          <div className="grid grid-cols-2 gap-3">
            <PropertyTile icon={AlertCircle} label="Priority">
              <span className="flex items-center gap-2">
                <PriorityGlyph level={priority.level} />
                {priority.label}
              </span>
            </PropertyTile>

            <PropertyTile icon={User} label="Assigned To">
              <span className="block truncate">
                {task.assignedToUserName ?? 'Unassigned'}
              </span>
            </PropertyTile>

            {task.dueDate && (
              <PropertyTile icon={Calendar} label="Due Date">
                {formatDateTime(task.dueDate)}
              </PropertyTile>
            )}

            {task.storyPoints && (
              <PropertyTile icon={Hash} label="Story Points">
                <span className="text-lg font-semibold">{task.storyPoints}</span>
              </PropertyTile>
            )}
          </div>

          {/* Description */}
          <div>
            <SectionHeading>Description</SectionHeading>
            <div className="card p-4">
              <p className="whitespace-pre-wrap text-sm text-foreground">{task.description}</p>
            </div>
          </div>

          {/* Acceptance Criteria */}
          {task.acceptanceCriteria && (
            <div>
              <SectionHeading>Acceptance Criteria</SectionHeading>
              <div className="card p-4">
                <p className="whitespace-pre-wrap text-sm text-foreground">
                  {task.acceptanceCriteria}
                </p>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <SectionHeading>Timeline</SectionHeading>
            <div className="grid grid-cols-2 gap-3">
              <PropertyTile icon={Calendar} label="Created">
                {formatDateTime(task.createdAt)}
              </PropertyTile>

              {task.startedAt && (
                <PropertyTile icon={Clock} label="Started">
                  {formatDateTime(task.startedAt)}
                </PropertyTile>
              )}

              {task.completedAt && (
                <PropertyTile icon={CheckCircle2} label="Completed">
                  {formatDateTime(task.completedAt)}
                </PropertyTile>
              )}

              <PropertyTile icon={User} label="Created By">
                <span className="block truncate">{task.createdByUserName ?? 'Unknown user'}</span>
              </PropertyTile>
            </div>
          </div>

          {/* Labels */}
          {task.labels.length > 0 && (
            <div>
              <SectionHeading>Labels</SectionHeading>
              <div className="flex flex-wrap items-center gap-2">
                {task.labels.map(label => (
                  <span key={label} className="badge-neutral gap-1">
                    <Tag className="h-3 w-3" aria-hidden="true" />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* AI Generation Details */}
          {task.isAiGenerated && task.aiGenerationPrompt && (
            <div>
              <SectionHeading>AI Generation Prompt</SectionHeading>
              <div className="card p-4">
                <p className="whitespace-pre-wrap text-sm text-foreground">
                  {task.aiGenerationPrompt}
                </p>
              </div>
            </div>
          )}

          {/* Comments */}
          {task.comments.length > 0 && (
            <div>
              <SectionHeading>
                <span className="flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                  Comments ({task.comments.length})
                </span>
              </SectionHeading>
              <div className="space-y-3">
                {task.comments.map(comment => (
                  <div key={comment.id} className="card p-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <User className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                        {comment.authorName ?? 'Unknown user'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(comment.createdAt)}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-foreground">{comment.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feed Item & Client Links */}
          {(task.feedItemId || task.clientId) && (
            <div className="border-t border-border pt-5">
              <SectionHeading>
                <span className="flex items-center gap-2">
                  <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Related Items
                </span>
              </SectionHeading>
              <div className="flex flex-wrap gap-3">
                {task.feedItemId && (
                  <Link to={`/feeds?item=${task.feedItemId}`} className="btn-outline btn-sm gap-1.5">
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    View Feed Item #{task.feedItemId}
                  </Link>
                )}
                {task.clientId && (
                  <Link to={`/clients?id=${task.clientId}`} className="btn-outline btn-sm gap-1.5">
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    View Client #{task.clientId}
                  </Link>
                )}
              </div>
            </div>
          )}
          </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          {isEditing ? (
            <>
              <button onClick={cancelEdit} disabled={saving} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-primary gap-2">
                {saving && <Spinner className="text-primary-foreground" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
